import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Crown,
  Search,
  ArrowLeft,
  Heart,
  Loader2,
  User,
  Target,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Trophy,
} from "lucide-react";
import { cn } from "../lib/utils";
import { BOSS_IMAGE_MAPPING } from "../utils/bossImages";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/* ============================================================================
 * Ore-themed seasons — each month is named after a real OreStore ore and uses
 * that ore's image as its icon. Hues are tuned to match the ore's appearance.
 * ============================================================================
 */
const ORE_MONTHS = [
  { idx: 0,  name: "Diamond",     hue: "#a8e8ff", image: "https://legends.ml/static/images/lib/item/04020007.png" },
  { idx: 1,  name: "Mithril",     hue: "#7da7c9", image: "https://legends.ml/static/images/lib/item/04010002.png" },
  { idx: 2,  name: "Emerald",     hue: "#5ed18a", image: "https://legends.ml/static/images/lib/item/04020003.png" },
  { idx: 3,  name: "Amethyst",    hue: "#c986ff", image: "https://legends.ml/static/images/lib/item/04020001.png" },
  { idx: 4,  name: "Aquamarine",  hue: "#5ed1d1", image: "https://legends.ml/static/images/lib/item/04020002.png" },
  { idx: 5,  name: "Topaz",       hue: "#ffd166", image: "https://legends.ml/static/images/lib/item/04020006.png" },
  { idx: 6,  name: "Gold",        hue: "#ffce4e", image: "https://legends.ml/static/images/lib/item/04010006.png" },
  { idx: 7,  name: "Garnet",      hue: "#ff7a7a", image: "https://legends.ml/static/images/lib/item/04020000.png" },
  { idx: 8,  name: "Adamantium",  hue: "#e09b5a", image: "https://legends.ml/static/images/lib/item/04010003.png" },
  { idx: 9,  name: "Opal",        hue: "#e5b3ff", image: "https://legends.ml/static/images/lib/item/04020004.png" },
  { idx: 10, name: "Silver",      hue: "#cfd6e0", image: "https://legends.ml/static/images/lib/item/04010004.png" },
  { idx: 11, name: "Sapphire",    hue: "#7a9dff", image: "https://legends.ml/static/images/lib/item/04020005.png" },
];

// Bronze is the entry-level ore — perfect for the Weeks group.
const BRONZE = {
  name: "Bronze",
  hue: "#d4a261",
  image: "https://legends.ml/static/images/lib/item/04010000.png",
};

// Black Crystal is the most legendary — fitting for Eternal/All-time.
const BLACK_CRYSTAL = {
  name: "Black Crystal",
  hue: "#caa55a",
  image: "https://legends.ml/static/images/lib/item/04020008.png",
};

/* ============================================================================
 * Helpers — date ranges for season picker
 * ============================================================================
 */
const startOfWeekUTC = (d, offsetWeeks = 0) => {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const day = (date.getUTCDay() + 6) % 7; // Monday=0
  date.setUTCDate(date.getUTCDate() - day - offsetWeeks * 7);
  date.setUTCHours(0, 0, 0, 0);
  return date;
};
const endOfWeekUTC = (d, offsetWeeks = 0) => {
  const s = startOfWeekUTC(d, offsetWeeks);
  const e = new Date(s);
  e.setUTCDate(e.getUTCDate() + 7);
  e.setUTCMilliseconds(-1);
  return e;
};
const startOfMonthUTC = (d, offsetMonths = 0) => {
  const dt = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - offsetMonths, 1));
  dt.setUTCHours(0, 0, 0, 0);
  return dt;
};
const endOfMonthUTC = (d, offsetMonths = 0) => {
  const dt = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - offsetMonths + 1, 1));
  dt.setUTCMilliseconds(-1);
  return dt;
};
const fmtRangeLabel = (start, end) => {
  const sm = start.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const em = end.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `${sm} → ${em}`;
};

/* ============================================================================
 * Build full season list — Weeks (W0..W-3), Moons (this..−5), All Time
 * ============================================================================
 */
const buildSeasons = () => {
  const now = new Date();
  const seasons = [];
  // Weeks — themed as Bronze Ore (the entry-level ore)
  for (let i = 0; i < 4; i++) {
    const s = startOfWeekUTC(now, i);
    const e = endOfWeekUTC(now, i);
    seasons.push({
      id: `week_${i}`,
      group: "weeks",
      label: i === 0 ? "This Week" : `${i}w ago`,
      sub: fmtRangeLabel(s, e),
      from: s.toISOString(),
      to: e.toISOString(),
      hue: BRONZE.hue,
      image: BRONZE.image,
    });
  }
  // Moons — each month is a different ore (current + 5 past).
  for (let i = 0; i < 6; i++) {
    const s = startOfMonthUTC(now, i);
    const e = endOfMonthUTC(now, i);
    const ore = ORE_MONTHS[s.getUTCMonth()];
    const realName = s.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    seasons.push({
      id: `moon_${i}`,
      group: "moons",
      label: ore.name,
      sub: realName,
      realMonth: realName,
      from: s.toISOString(),
      to: e.toISOString(),
      hue: ore.hue,
      image: ore.image,
      isCurrent: i === 0,
    });
  }
  // All time — themed as Black Crystal (the legendary ore)
  seasons.push({
    id: "all_time",
    group: "ages",
    label: BLACK_CRYSTAL.name,
    sub: "All-time legacy",
    hue: BLACK_CRYSTAL.hue,
    image: BLACK_CRYSTAL.image,
    period: "all_time",
  });
  return seasons;
};

/* ============================================================================
 * Boss helpers — for visual engraving on boss-specialist mode
 * ============================================================================
 */
const getBossImage = (bossName) => {
  if (!bossName) return null;
  const base = bossName.split(" - ")[0].trim();
  return BOSS_IMAGE_MAPPING[base] || null;
};

/* Default user avatar — matches the profile page fallback exactly:
 * a circular `bg-primary/20` orb with a Lucide `<User>` icon in primary color.
 * Used everywhere a hunter has no avatar, OR when their avatar URL fails to load.
 */
const UserAvatar = ({ src, alt = "", sizeClass = "w-7 h-7", iconSize = "w-4 h-4", roundedClass = "rounded-full" }) => {
  const [broken, setBroken] = useState(!src);
  if (!src || broken) {
    return (
      <div
        className={cn(
          "bg-primary/20 border border-border/60 flex items-center justify-center shrink-0",
          sizeClass,
          roundedClass
        )}
        aria-label={alt || "User avatar placeholder"}
      >
        <User className={cn("text-primary", iconSize)} />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      onError={() => setBroken(true)}
      className={cn("object-cover border border-border/60 shrink-0", sizeClass, roundedClass)}
    />
  );
};

/* ============================================================================
 * Parchment scroll wrapper — kept as a gold-accented "scroll" card but tuned
 * to sit on a dark tracker background. Subtle parchment gradient + amber border
 * (no green jungle).
 * ============================================================================
 */
const ScrollFrame = ({ children, hue = "#caa55a", bossWatermark = null }) => (
  <div className="relative mx-auto" style={{ maxWidth: 760 }}>
    {/* Top finial */}
    <div className="absolute -top-3 left-0 right-0 flex justify-between pointer-events-none z-10">
      <ScrollKnob />
      <ScrollKnob />
    </div>
    {/* Bottom finial */}
    <div className="absolute -bottom-3 left-0 right-0 flex justify-between pointer-events-none z-10">
      <ScrollKnob />
      <ScrollKnob />
    </div>
    <div
      className="relative rounded-md border-[3px] shadow-2xl overflow-hidden"
      style={{
        borderColor: hue,
        background:
          "linear-gradient(180deg, #1c1c2d 0%, #15151f 60%, #0e0e16 100%)",
        boxShadow:
          `inset 0 0 60px ${hue}1a, 0 14px 40px rgba(0,0,0,0.5)`,
      }}
    >
      {bossWatermark && (
        <img
          src={bossWatermark}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
          style={{
            opacity: 0.08,
            filter: "saturate(0.5)",
            imageRendering: "pixelated",
            transform: "scale(0.9)",
          }}
        />
      )}
      <div
        className="relative z-[1] px-4 sm:px-8 py-4 sm:py-6"
        style={{ color: "#e8d9b0" }}
      >
        {children}
      </div>
    </div>
    {/* Top rolled edge */}
    <div
      className="absolute -top-1 left-6 right-6 h-2 rounded-full"
      style={{ background: "linear-gradient(180deg, #d6b25a 0%, #a87a2c 100%)" }}
    />
    {/* Bottom rolled edge */}
    <div
      className="absolute -bottom-1 left-6 right-6 h-2 rounded-full"
      style={{ background: "linear-gradient(180deg, #a87a2c 0%, #d6b25a 100%)" }}
    />
  </div>
);

const ScrollKnob = () => (
  <span
    aria-hidden="true"
    className="inline-block rounded-full"
    style={{
      width: 28,
      height: 28,
      background:
        "radial-gradient(circle at 35% 35%, #ffe89a 0%, #c9a04a 55%, #6b4c14 100%)",
      boxShadow: "0 2px 6px rgba(0,0,0,0.4), inset 0 0 6px rgba(60,40,10,0.6)",
      border: "2px solid #5a3a14",
    }}
  />
);

/* Decorative golden filigree divider */
const Filigree = ({ small = false, color = "#caa55a" }) => (
  <svg
    viewBox="0 0 400 24"
    preserveAspectRatio="none"
    className={cn("w-full block opacity-70", small ? "h-3" : "h-5")}
    aria-hidden="true"
  >
    <path
      d="M0 12 L60 12 M60 12 Q70 2 80 12 T100 12 Q110 22 120 12 T160 12 Q200 0 200 12 Q200 24 240 12 T280 12 Q290 22 300 12 T320 12 Q330 2 340 12 L400 12"
      stroke={color}
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
    />
    <circle cx="200" cy="12" r="3" fill={color} />
    <circle cx="100" cy="12" r="1.5" fill={color} />
    <circle cx="300" cy="12" r="1.5" fill={color} />
  </svg>
);

/* ============================================================================
 * Top-1 hero row (the crowned champion)
 * ============================================================================
 */
const ChampionRow = ({ row, onClick }) => {
  if (!row) return null;
  return (
    <div className="relative px-1 pb-2 sm:pb-3">
      {/* Crown */}
      <div className="absolute -top-3 sm:-top-5 left-1/2 -translate-x-1/2 z-10">
        <div
          className="text-3xl sm:text-4xl drop-shadow-md"
          style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.6))" }}
          aria-hidden="true"
        >
          👑
        </div>
      </div>
      <div
        onClick={onClick}
        className={cn(
          "mt-3 sm:mt-4 group cursor-pointer flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 sm:py-4 rounded-md border-2 transition",
          "hover:scale-[1.01]"
        )}
        style={{
          borderColor: "#d4a534",
          background:
            "linear-gradient(135deg, rgba(212,165,52,0.18) 0%, rgba(120,80,20,0.1) 100%)",
          boxShadow:
            "inset 0 0 18px rgba(212,165,52,0.18), 0 3px 0 rgba(120,80,20,0.25)",
        }}
        data-testid="champion-row"
      >
        <div className="text-[26px] sm:text-[32px] font-extrabold font-mono text-amber-300 tracking-tight w-12 text-center select-none">
          01
        </div>
        <UserAvatar
          src={row.avatar}
          alt={row.username}
          sizeClass="w-12 h-12 sm:w-14 sm:h-14"
          iconSize="w-7 h-7"
          roundedClass="rounded-md"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              className="font-extrabold text-xl sm:text-2xl truncate text-amber-100"
              style={{ fontFamily: "'Cinzel', Georgia, serif" }}
            >
              {row.username}
            </span>
            {row.is_kofi_supporter && (
              <Heart className="w-4 h-4 text-rose-400 fill-rose-400 shrink-0" />
            )}
          </div>
          <div className="text-[11px] sm:text-xs text-amber-200/60 mt-0.5 font-mono">
            {row.unique_bosses} unique boss{row.unique_bosses !== 1 ? "es" : ""}
            {row.last_kill_at && (
              <> · last kill {new Date(row.last_kill_at).toLocaleDateString()}</>
            )}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div
            className="font-extrabold text-2xl sm:text-3xl font-mono text-amber-100"
            data-testid="champion-kills"
          >
            {row.kills.toLocaleString()}
          </div>
          <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] font-extrabold text-amber-200/60">
            kills
          </div>
        </div>
      </div>
    </div>
  );
};

/* ============================================================================
 * Ranked list row (#02 and beyond) — MapleStory scroll style
 * ============================================================================
 */
const RankRow = ({ row, onClick }) => {
  return (
    <div
      onClick={onClick}
      className="group cursor-pointer grid grid-cols-[48px_28px_1fr_auto] items-center gap-2 sm:gap-3 px-2 sm:px-4 py-2 transition hover:bg-amber-500/10"
      style={{ borderBottom: "1px dashed rgba(202, 165, 90, 0.18)" }}
      data-testid={`rank-row-${row.rank}`}
    >
      <span
        className="font-mono font-extrabold text-lg sm:text-xl text-center select-none"
        style={{ color: row.rank <= 3 ? "#ffce6b" : "#caa55a" }}
      >
        {String(row.rank).padStart(2, "0")}
      </span>
      <UserAvatar src={row.avatar} alt={row.username} sizeClass="w-7 h-7" iconSize="w-4 h-4" roundedClass="rounded-md" />
      <div className="min-w-0 flex items-center gap-1.5">
        <span
          className="font-bold truncate text-base sm:text-lg text-amber-50"
          style={{ fontFamily: "'Cinzel', Georgia, serif" }}
          data-testid={`rank-name-${row.rank}`}
        >
          {row.username}
        </span>
        {row.is_kofi_supporter && (
          <Heart className="w-3.5 h-3.5 text-rose-400 fill-rose-400 shrink-0" />
        )}
        <span className="text-[10px] text-amber-200/40 hidden sm:inline font-mono">
          · {row.unique_bosses} boss{row.unique_bosses !== 1 ? "es" : ""}
        </span>
      </div>
      <span
        className="font-mono font-extrabold text-base sm:text-lg text-right tabular-nums whitespace-nowrap text-amber-100"
        data-testid={`rank-kills-${row.rank}`}
      >
        {row.kills.toLocaleString()}
      </span>
    </div>
  );
};

/* ============================================================================
 * Season picker — fancy popover with three groups (Weeks / Moons / Eternal)
 * ============================================================================
 */
const SeasonPicker = ({ seasons, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const cur = seasons.find((s) => s.id === value) || seasons[0];

  // Quick prev/next within the same group
  const groupSeasons = useMemo(
    () => seasons.filter((s) => s.group === cur.group),
    [seasons, cur.group]
  );
  const idxInGroup = groupSeasons.findIndex((s) => s.id === cur.id);
  const canPrev = idxInGroup > 0;
  const canNext = idxInGroup >= 0 && idxInGroup < groupSeasons.length - 1;

  return (
    <div className="flex items-center gap-1.5">
      <Button
        variant="outline"
        size="icon"
        onClick={() => canPrev && onChange(groupSeasons[idxInGroup - 1].id)}
        disabled={!canPrev}
        className="h-9 w-9"
        data-testid="season-prev"
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <div className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md border-2 border-amber-500/40 bg-card hover:bg-accent text-foreground min-w-[180px] text-left transition"
          data-testid="season-picker-trigger"
        >
          <span aria-hidden="true">
            {cur.image ? <OreIcon src={cur.image} size={18} hue={cur.hue} /> : null}
          </span>
          <div className="flex-1 min-w-0">
            <div
              className="font-extrabold text-base leading-tight truncate"
              style={{ color: cur.hue, fontFamily: "'Cinzel', Georgia, serif" }}
            >
              {cur.label}
            </div>
            <div className="text-[10px] text-muted-foreground truncate font-mono">
              {cur.sub}
            </div>
          </div>
          <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
        </button>
        {open && (
          <div
            className="absolute z-30 mt-1 left-0 sm:left-auto right-0 w-[280px] rounded-md border-2 border-amber-500/40 bg-card shadow-2xl max-h-[420px] overflow-y-auto"
            data-testid="season-picker-popover"
          >
            {["weeks", "moons", "ages"].map((grp) => {
              const items = seasons.filter((s) => s.group === grp);
              if (items.length === 0) return null;
              const grpLabel = grp === "weeks" ? "Weeks" : grp === "moons" ? "Moons" : "Eternal";
              return (
                <div key={grp} className="py-1">
                  <div className="px-3 py-1 text-[10px] uppercase tracking-[0.25em] font-extrabold text-amber-400">
                    {grpLabel}
                  </div>
                  {items.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => {
                        onChange(s.id);
                        setOpen(false);
                      }}
                      className={cn(
                        "w-full flex items-center gap-2 px-3 py-2 text-left transition hover:bg-accent",
                        s.id === cur.id && "bg-accent"
                      )}
                      data-testid={`season-option-${s.id}`}
                    >
                      <span aria-hidden="true">
                        {s.image ? <OreIcon src={s.image} size={18} hue={s.hue} /> : null}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div
                          className="font-extrabold text-sm leading-tight truncate"
                          style={{ color: s.hue, fontFamily: "'Cinzel', Georgia, serif" }}
                        >
                          {s.label}
                        </div>
                        <div className="text-[10px] text-muted-foreground truncate font-mono">
                          {s.sub}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Button
        variant="outline"
        size="icon"
        onClick={() => canNext && onChange(groupSeasons[idxInGroup + 1].id)}
        disabled={!canNext}
        className="h-9 w-9"
        data-testid="season-next"
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  );
};

/* Tiny ore icon used as a season "emoji" replacement */
const OreIcon = ({ src, alt = "", size = 18, glow = false, hue = "#caa55a" }) => (
  <img
    src={src}
    alt={alt}
    width={size}
    height={size}
    className="inline-block object-contain shrink-0"
    style={{
      imageRendering: "pixelated",
      filter: glow ? `drop-shadow(0 0 6px ${hue}aa)` : "none",
    }}
  />
);

/* ============================================================================
 * Current season banner — emphasizes the active season with hue accent.
 * Ore image front-and-center, real month name underneath.
 * ============================================================================
 */
const CurrentSeasonBanner = ({ season }) => {
  if (!season) return null;
  const realLabel =
    season.realMonth ||
    (season.from && season.to
      ? `${new Date(season.from).toLocaleDateString(undefined, { month: "short", day: "numeric" })} — ${new Date(season.to).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
      : season.sub);
  return (
    <div
      className="relative rounded-xl border overflow-hidden"
      style={{
        borderColor: `${season.hue}55`,
        background: `linear-gradient(135deg, ${season.hue}1a 0%, transparent 40%, ${season.hue}0d 100%)`,
      }}
      data-testid="current-season-banner"
    >
      <div
        className="absolute inset-y-0 left-0 w-1.5"
        style={{ background: season.hue, boxShadow: `0 0 18px ${season.hue}80` }}
        aria-hidden="true"
      />
      <div className="px-5 sm:px-6 py-4 flex items-center gap-4 flex-wrap">
        <div
          className="flex items-center justify-center rounded-full shrink-0"
          style={{
            width: 64,
            height: 64,
            background: `radial-gradient(circle at 35% 35%, ${season.hue}55 0%, ${season.hue}22 60%, transparent 100%)`,
            boxShadow: `inset 0 0 12px ${season.hue}55, 0 0 18px ${season.hue}55`,
          }}
        >
          {season.image ? (
            <OreIcon src={season.image} size={42} glow hue={season.hue} alt={season.label} />
          ) : (
            <span className="text-3xl" aria-hidden="true">⚒</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] font-extrabold text-muted-foreground">
            Current Season
          </div>
          <div className="flex items-baseline gap-2 flex-wrap mt-0.5">
            <h2
              className="text-2xl sm:text-3xl font-extrabold leading-tight"
              style={{ color: season.hue, textShadow: `0 0 18px ${season.hue}44` }}
              data-testid="current-season-name"
            >
              {season.label}
            </h2>
            {season.group === "moons" && (
              <span
                className="text-base sm:text-lg font-bold text-muted-foreground"
                data-testid="current-season-real-month"
              >
                ({realLabel})
              </span>
            )}
          </div>
          {season.group !== "moons" && (
            <div className="text-xs text-muted-foreground mt-0.5">
              {realLabel}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ============================================================================
 * Main Rankings page
 * ============================================================================
 */
const RankingsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const seasons = useMemo(() => buildSeasons(), []);
  // Default to current Moon (current real-world month) instead of This Week.
  const [seasonId, setSeasonId] = useState(searchParams.get("season") || "moon_0");
  const [tab, setTab] = useState(searchParams.get("tab") || "overall");
  const [bossFilter, setBossFilter] = useState(searchParams.get("boss") || "");
  const [search, setSearch] = useState("");

  const [leaderboard, setLeaderboard] = useState([]);
  const [loadingLb, setLoadingLb] = useState(false);
  const [topBosses, setTopBosses] = useState([]);
  const [allBosses, setAllBosses] = useState([]);

  const season = useMemo(
    () => seasons.find((s) => s.id === seasonId) || seasons[0],
    [seasons, seasonId]
  );

  useEffect(() => {
    const params = { tab, season: seasonId };
    if (bossFilter) params.boss = bossFilter;
    setSearchParams(params, { replace: true });
  }, [tab, seasonId, bossFilter, setSearchParams]);

  const loadLeaderboard = useCallback(async () => {
    setLoadingLb(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (season.period === "all_time" || season.id === "all_time") {
        params.set("period", "all_time");
      } else if (season.from && season.to) {
        params.set("period", "custom");
        params.set("date_from", season.from);
        params.set("date_to", season.to);
      }
      if (tab === "by-boss" && bossFilter) params.set("boss_name", bossFilter);
      const r = await axios.get(`${API_URL}/api/rankings/kills?${params.toString()}`);
      setLeaderboard(r.data?.leaderboard || []);
    } catch (e) {
      setLeaderboard([]);
    } finally {
      setLoadingLb(false);
    }
  }, [season, tab, bossFilter]);

  const loadTopBosses = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: "30" });
      if (season.period === "all_time" || season.id === "all_time") {
        params.set("period", "all_time");
      } else if (season.from && season.to) {
        params.set("period", "custom");
        params.set("date_from", season.from);
        params.set("date_to", season.to);
      }
      const r = await axios.get(`${API_URL}/api/rankings/bosses?${params.toString()}`);
      setTopBosses(r.data?.bosses || []);
    } catch (e) {
      setTopBosses([]);
    }
  }, [season]);

  // Load full boss list for filter dropdown
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/bosses`);
        const list = r.data || [];
        const names = [...new Set(list.map((b) => (b?.name || "").split(" - ")[0]).filter(Boolean))].sort();
        setAllBosses(names);
      } catch (e) {
        setAllBosses([]);
      }
    })();
  }, []);

  useEffect(() => {
    loadLeaderboard();
  }, [loadLeaderboard]);
  useEffect(() => {
    loadTopBosses();
  }, [loadTopBosses]);

  const filteredLb = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return leaderboard;
    return leaderboard.filter((r) => (r.username || "").toLowerCase().includes(q));
  }, [leaderboard, search]);

  const totalKills = useMemo(
    () => leaderboard.reduce((sum, r) => sum + (r.kills || 0), 0),
    [leaderboard]
  );

  const champion = filteredLb[0];
  const restRows = filteredLb.slice(1);
  const bossWatermark = tab === "by-boss" && bossFilter ? getBossImage(bossFilter) : null;

  return (
    <div
      className="min-h-screen text-foreground bg-background"
    >
      {/* Header bar */}
      <header className="border-b border-border bg-card/60 backdrop-blur-sm sticky top-0 z-30">
        <div className="container mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            data-testid="rankings-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Trophy className="w-6 h-6 text-amber-400 drop-shadow-[0_0_8px_rgba(251,191,36,0.6)]" />
            <div>
              <h1
                className="text-xl sm:text-2xl font-extrabold leading-tight"
                style={{ letterSpacing: "0.02em" }}
                data-testid="rankings-title"
              >
                OreBossTracker <span className="text-amber-400">Ranking</span>
              </h1>
              <p className="text-[10px] sm:text-xs text-muted-foreground -mt-0.5">
                Top hunters by kill count
              </p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <SeasonPicker seasons={seasons} value={seasonId} onChange={setSeasonId} />
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Find hunter…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9 w-40"
                data-testid="rankings-search-input"
              />
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 pb-12 space-y-6">
        {/* Current-season banner */}
        <CurrentSeasonBanner season={season} />

        {/* Tab pills */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="rankings-tabs">
          <button
            type="button"
            onClick={() => setTab("overall")}
            className={cn(
              "px-4 py-1.5 rounded-full font-extrabold text-sm border-2 transition",
              tab === "overall"
                ? "bg-amber-500 text-amber-950 border-amber-500"
                : "bg-card text-muted-foreground border-border hover:border-amber-500/60 hover:text-foreground"
            )}
            data-testid="tab-overall"
          >
            <Crown className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Overall
          </button>
          <button
            type="button"
            onClick={() => setTab("by-boss")}
            className={cn(
              "px-4 py-1.5 rounded-full font-extrabold text-sm border-2 transition",
              tab === "by-boss"
                ? "bg-amber-500 text-amber-950 border-amber-500"
                : "bg-card text-muted-foreground border-border hover:border-amber-500/60 hover:text-foreground"
            )}
            data-testid="tab-by-boss"
          >
            <Target className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            By Boss
          </button>

          {tab === "by-boss" && (
            <div className="flex items-center gap-2 ml-1 flex-wrap">
              <select
                value={bossFilter}
                onChange={(e) => setBossFilter(e.target.value)}
                className="h-9 px-3 rounded-full text-sm font-bold bg-card text-foreground border-2 border-border max-w-[220px]"
                data-testid="boss-filter-select"
              >
                <option value="">— pick a boss —</option>
                {allBosses.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
              {bossFilter && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setBossFilter("")}
                  className="h-9 rounded-full"
                >
                  Clear
                </Button>
              )}
            </div>
          )}

          <div className="ml-auto text-[11px] text-muted-foreground italic">
            {season.label} · {filteredLb.length} hunter{filteredLb.length !== 1 ? "s" : ""} · {totalKills.toLocaleString()} total kills
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_300px] gap-5">
          <ScrollFrame hue={season.hue} bossWatermark={bossWatermark}>
            {/* Season title block */}
            <div className="text-center mb-3 sm:mb-4">
              <div
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-extrabold uppercase tracking-[0.25em]"
                style={{
                  color: season.hue,
                  background: `linear-gradient(90deg, transparent 0%, ${season.hue}33 50%, transparent 100%)`,
                  textShadow: `0 0 12px ${season.hue}55`,
                }}
              >
                {season.image && <OreIcon src={season.image} size={14} hue={season.hue} />}
                <span>{tab === "by-boss" && bossFilter ? `${bossFilter} · ` : ""}{season.label}</span>
              </div>
              <div className="text-[10px] text-muted-foreground mt-1 font-mono">
                {season.realMonth || season.sub}
              </div>
            </div>
            <Filigree color={season.hue} />

            {/* Crown + champion */}
            {loadingLb ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Unfurling the scroll…
              </div>
            ) : filteredLb.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground italic">
                {tab === "by-boss" && !bossFilter
                  ? "Choose a boss above to reveal its champions."
                  : "No deeds recorded in this season yet."}
              </div>
            ) : (
              <>
                <ChampionRow row={champion} />
                <Filigree small color={season.hue} />
                {/* Rest of the list */}
                <div className="mt-2 sm:mt-3" data-testid="rankings-list">
                  {restRows.map((row) => (
                    <RankRow key={row.user_id} row={row} />
                  ))}
                </div>
              </>
            )}
            <Filigree small color={season.hue} />
            <div className="text-center text-[10px] sm:text-[11px] text-muted-foreground italic mt-2">
              ✦ Ranks update in real-time as kills are recorded ✦
            </div>
          </ScrollFrame>

          {/* Hottest Bosses sidebar */}
          <aside className="lg:sticky lg:top-4 self-start space-y-3">
            <div
              className="rounded-md border-2 border-amber-500/30 bg-card p-3"
              data-testid="hot-bosses-card"
            >
              <h3
                className="font-extrabold text-base mb-1.5 flex items-center gap-1.5 text-amber-100"
                style={{ fontFamily: "'Cinzel', Georgia, serif" }}
              >
                <Target className="w-4 h-4 text-amber-400" /> Hottest Bosses
              </h3>
              <div className="text-[10px] text-muted-foreground mb-1.5 italic">
                Hunted most in <strong className="text-amber-300">{season.label}</strong>
              </div>
              <Filigree small color={season.hue} />
              {topBosses.length === 0 ? (
                <div className="text-xs text-muted-foreground italic py-2">
                  No boss kills recorded yet for this season.
                </div>
              ) : (
                <div className="space-y-0.5 mt-1.5 max-h-[480px] overflow-y-auto pr-1">
                  {topBosses.slice(0, 20).map((b, i) => {
                    const img = getBossImage(b.boss_name);
                    return (
                      <button
                        key={b.boss_name}
                        type="button"
                        onClick={() => {
                          setBossFilter(b.boss_name);
                          setTab("by-boss");
                        }}
                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-left hover:bg-accent transition group"
                        data-testid={`hot-boss-${i}`}
                      >
                        <span
                          className="w-7 text-center text-xs font-mono font-extrabold"
                          style={{ color: i < 3 ? "#ffce6b" : "#caa55a" }}
                        >
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        {img ? (
                          <img
                            src={img}
                            alt=""
                            className="w-7 h-7 object-contain shrink-0"
                            style={{ imageRendering: "pixelated" }}
                          />
                        ) : (
                          <Target className="w-5 h-5 text-amber-400 shrink-0" />
                        )}
                        <span
                          className="flex-1 text-sm font-bold truncate text-amber-50"
                          style={{ fontFamily: "'Cinzel', Georgia, serif" }}
                        >
                          {b.boss_name}
                        </span>
                        <span className="text-xs font-mono text-muted-foreground tabular-nums">
                          {b.kills.toLocaleString()}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default RankingsPage;
